import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormControlLabel as MuiFormControlLabel,
  Alert,
  AlertTitle,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  Link,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import { useFormik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { api } from '../../services/api';
import { deliveryZoneService } from '../../services/delivery-zones';
import BPointPayment from '../payment/BPointPayment';
import PaymentSummary from '../payment/PaymentSummary';
import PaymentAmountSelector from '../payment/PaymentAmountSelector';
import { formatCalendarDate, formatTimeOfDay } from '../../utils/dateTime';
import type { PaymentSummary as PaymentSummaryType } from '../../types/payment';

interface CustomerDetails {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  suburb: string;
  postcode: string;
  notes: string;
}

interface PickupSlot {
  id: number;
  start_time: string;
  end_time: string;
  available_capacity: number;
  total_capacity: number;
}

interface OrderData {
  foodParcel: {
    id: number;
    name: string;
    price: number;
  };
  orderConfiguration?: {
    configuration: {
      bundle_selections: (number | null)[];
      bread_selection: number | null;
      grocery_selections: (number | null)[];
        parcel_item_selections: Record<number, boolean>;
      addon_product_selections?: Record<number, number>;
    };
    totalCalculation: {
      total: number;
      breakdown: {
        food_parcel: { name: string; price: number };
        bundles?: { total: number; count: number };
        bread?: { name: string; price: number };
        grocery?: { total: number; items: Array<{ name: string; price: number }> };
        parcel_items?: { total: number; items: Array<{ name: string; price: number }> };
        addon_products?: { total: number; items: Array<{ name: string; quantity: number; unit_price: number; line_total: number }> };
      };
    };
  };
  order?: {
    id: number;
    order_number: string;
    total_amount: number;
    foodParcel: {
      id: number;
      name: string;
      price: number;
    };
    orderItems: Array<{
      id: number;
      item_type: string;
      selection_reference: string;
      quantity: number;
      unit_price: number;
      total_price: number;
      product?: {
        id: number;
        name: string;
        food_parcel_price: number;
      };
      productBundle?: {
        id: number;
        name: string;
        bundle_price: number;
      };
      product_snapshot?: any;
    }>;
    configuration?: {
      id: number;
      bundle_selection_1: number | null;
      bundle_selection_2: number | null;
      bundle_selection_3: number | null;
      bread_selection: number | null;
      grocery_selection_1: number | null;
      grocery_selection_2: number | null;
      parcel_item_selections: Record<number, boolean>;
      bundleSelection1?: any;
      bundleSelection2?: any;
      bundleSelection3?: any;
      breadSelection?: any;
      grocerySelection1?: any;
      grocerySelection2?: any;
    };
  };
  assignedParcelDay: {
    id: number;
    date: string;
    weekday: string;
    is_currently_open: boolean;
    status: 'open' | 'upcoming' | 'future';
    status_text: string;
    color_code: 'green' | 'orange' | 'red' | 'blue' | 'grey';
    order_time: any;
    pickup_schedule: any;
    notes: string | null;
  } | null;
  customerDetails: CustomerDetails;
  totalAmount?: number;
  orderId?: number;
  orderNumber?: string;
}

interface CheckoutPageProps {
  orderData: OrderData;
  onBack: () => void;
  onSubmit: (checkoutData: any) => void;
  onCancel?: () => void;
  cancelling?: boolean;
}

interface CheckoutFormData {
  // Customer details (editable)
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  suburb: string;
  postcode: string;
  notes: string;
  
  // Delivery method
  deliveryMethod: 'pickup' | 'delivery';
  
  // Pickup details
  pickupSlotId?: number;
  
  // Payment method
  paymentMethod: 'direct_deposit' | 'card_payment';
  
  // Voucher amount
  voucherAmount: string;
  
  // Order comment (free text from customer)
  customerComment: string;
  
  // Terms acceptance
  termsAccepted: boolean;
}

const CheckoutPage: React.FC<CheckoutPageProps> = ({
  orderData,
  onBack,
  onSubmit,
  onCancel,
  cancelling,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const paymentReturnHandled = useRef(false);
  const paymentSummaryInitializedRef = useRef(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availablePickupSlots, setAvailablePickupSlots] = useState<PickupSlot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [deliveryZoneValidation, setDeliveryZoneValidation] = useState<{
    isValid: boolean;
    message: string;
    zone?: any;
  } | null>(null);
  const [validatingPostcode, setValidatingPostcode] = useState(false);
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [deliveryFeeLoading, setDeliveryFeeLoading] = useState(false);
  const [deliveryOptionDisabled, setDeliveryOptionDisabled] = useState(false);
  const [deliveryDisabledMessage, setDeliveryDisabledMessage] = useState('');
  const [bpointAvailable, setBpointAvailable] = useState<boolean | null>(null);
  const [bpointLoading, setBpointLoading] = useState(true);
  const [cardPaymentCompleted, setCardPaymentCompleted] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'paid'>('pending');
  const [paymentSummary, setPaymentSummary] = useState<PaymentSummaryType | null>(null);
  const [customPaymentAmount, setCustomPaymentAmount] = useState<number>(0);
  const [isCustomPayment, setIsCustomPayment] = useState<boolean>(false);
  const [paymentSummaryRefreshKey, setPaymentSummaryRefreshKey] = useState<number>(0);
  const [resetPaymentSelector, setResetPaymentSelector] = useState<boolean>(false);
  const [verifyingPayment, setVerifyingPayment] = useState(false);
  const [paymentFailedAttempt, setPaymentFailedAttempt] = useState(false);
  const customerEmailRef = useRef<string>('');
  const deliveryMethodRef = useRef<string>('pickup');
  const postcodeRef = useRef<string>('');

  // Debug logging
  console.log('CheckoutPage - orderData:', orderData);
  console.log('CheckoutPage - orderData.order:', orderData.order);
  console.log('CheckoutPage - orderData.orderId:', orderData.orderId);

  // Apply payment return result (shared by URL params and popup postMessage)
  const applyPaymentReturn = useCallback((payment: string) => {
    if (payment === 'success') {
      setPaymentFailedAttempt(false);
      setCardPaymentCompleted(true);
      setPaymentStatus('paid');
      setPaymentSummaryRefreshKey(prev => prev + 1);
      setResetPaymentSelector(true);
      setTimeout(() => setResetPaymentSelector(false), 100);
      setError('Payment received. Your card payment has been recorded. Please review your order and click "Place Order" to complete.');
    } else {
      setPaymentFailedAttempt(true);
      setCardPaymentCompleted(false);
      setPaymentStatus('pending');
      setPaymentSummaryRefreshKey(prev => prev + 1);
      setError(
        payment === 'cancelled'
          ? 'Payment was cancelled. You can try again or select a different payment method.'
          : 'Payment failed. Please try again or select a different payment method.'
      );
    }
  }, []);

  // Verify with backend that order is paid before showing success (architect: payment validation)
  const verifyAndApplyPaymentSuccess = useCallback(async (orderId: number, email?: string) => {
    setVerifyingPayment(true);
    setError(null);
    try {
      const url = email
        ? `/v1/payment/orders/${orderId}/summary?email=${encodeURIComponent(email)}`
        : `/v1/payment/orders/${orderId}/summary`;
      const response = await api.get(url);
      if (response.data?.success && response.data?.data) {
        const status = response.data.data.payment_status;
        if (status === 'fully_paid' || status === 'partially_paid') {
          applyPaymentReturn('success');
          return;
        }
      }
      setError('Payment could not be confirmed. Please refresh or try again.');
    } catch {
      setError('Payment could not be confirmed. Please refresh or try again.');
    } finally {
      setVerifyingPayment(false);
    }
  }, [applyPaymentReturn]);

  // Listen for popup return: payment window posts message then closes
  // Handles (1) redirect flow: bpoint_payment_return with payment=success|cancelled|failed
  // (2) mock flow: payment_success / payment_failed
  // For payment=success we verify with API before applying (payment validation).
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const t = event.data?.type;
      if (t === 'bpoint_payment_return') {
        const { payment, order_id: msgOrderId } = event.data;
        if (payment !== 'success' && payment !== 'cancelled' && payment !== 'failed') return;
        if (payment === 'success') {
          const orderId = msgOrderId ?? orderData.orderId;
          if (!orderId) {
            setError('Payment could not be confirmed. Please refresh or try again.');
            return;
          }
          verifyAndApplyPaymentSuccess(Number(orderId), customerEmailRef.current || undefined);
          return;
        }
        applyPaymentReturn(payment);
        return;
      }
      if (t === 'payment_success') {
        const orderId = event.data?.order_id ?? orderData.orderId;
        if (!orderId) {
          setError('Payment could not be confirmed. Please refresh or try again.');
          return;
        }
        verifyAndApplyPaymentSuccess(Number(orderId), customerEmailRef.current || undefined);
        return;
      }
      if (t === 'payment_failed') {
        applyPaymentReturn('failed');
        return;
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [applyPaymentReturn, verifyAndApplyPaymentSuccess, orderData.orderId]);

  // Handle return from BPOINT when opened in same tab: URL has payment=success|cancelled|failed
  // For payment=success we verify with API before applying (payment validation).
  useEffect(() => {
    const payment = searchParams.get('payment');
    if (!payment || paymentReturnHandled.current) return;
    if (payment !== 'success' && payment !== 'cancelled' && payment !== 'failed') return;

    paymentReturnHandled.current = true;

    const clearPaymentParams = () => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('payment');
        next.delete('order_id');
        return next;
      }, { replace: true });
    };

    if (payment === 'success') {
      const orderIdParam = searchParams.get('order_id');
      const orderId = orderIdParam ? Number(orderIdParam) : orderData.orderId;
      if (!orderId) {
        setError('Payment could not be confirmed. Please refresh or try again.');
        clearPaymentParams();
        return;
      }
      verifyAndApplyPaymentSuccess(Number(orderId), customerEmailRef.current || undefined).finally(clearPaymentParams);
      return;
    }

    applyPaymentReturn(payment);
    clearPaymentParams();
  }, [searchParams, setSearchParams, applyPaymentReturn, verifyAndApplyPaymentSuccess, orderData.orderId]);

  // Load order data if not available
  useEffect(() => {
    if (!orderData.order && orderData.orderId) {
      console.log('CheckoutPage - Loading order data...');
      loadOrderData();
    }
  }, [orderData.orderId]);

  const loadOrderData = async () => {
    if (!orderData.orderId) return;
    
    try {
      const response = await api.get(`/v1/orders/${orderData.orderId}`);
      console.log('CheckoutPage - Order data loaded:', response.data);
      // Note: We can't update the parent state from here, so this won't work
      // The order data should be loaded in the parent component
    } catch (err: any) {
      console.error('Failed to load order data:', err);
      setError('Failed to load order details');
    }
  };



  const loadAvailablePickupSlots = useCallback(async () => {
    console.log('🔍 DEBUG: loadAvailablePickupSlots called');
    console.log('🔍 DEBUG: orderData.assignedParcelDay:', orderData.assignedParcelDay);
    console.log('🔍 DEBUG: pickup_schedule?.id:', orderData.assignedParcelDay?.pickup_schedule?.id);
    
    if (!orderData.assignedParcelDay?.pickup_schedule?.id) {
      console.log('🔍 DEBUG: No pickup schedule ID found, returning early');
      return;
    }
    
    const parcelDayId = orderData.assignedParcelDay.id;
    console.log('🔍 DEBUG: Loading pickup slots for parcel day ID:', parcelDayId);
    
    setLoadingSlots(true);
    try {
      console.log('🔍 DEBUG: Making API call to /parcel-days/' + parcelDayId + '/available-pickup-slots');
      const response = await api.get(`/v1/parcel-days/${parcelDayId}/available-pickup-slots`);
      console.log('🔍 DEBUG: API response received:', response);
      console.log('🔍 DEBUG: Response data:', response.data);
      console.log('🔍 DEBUG: Response data.data:', response.data.data);
      
      const slots = response.data.data || [];
      console.log('🔍 DEBUG: Processed slots array:', slots);
      console.log('🔍 DEBUG: Number of slots:', slots.length);
      
      // Log each slot individually
      slots.forEach((slot: any, index: number) => {
        console.log(`🔍 DEBUG: Slot ${index + 1}:`, {
          id: slot.id,
          start_time: slot.start_time,
          end_time: slot.end_time,
          available_capacity: slot.available_capacity,
          total_capacity: slot.total_capacity,
          max_capacity: slot.max_capacity,
          booked_capacity: slot.booked_capacity
        });
      });
      
      setAvailablePickupSlots(slots);
      console.log('🔍 DEBUG: State updated with slots');
    } catch (err: any) {
      console.error('🔍 DEBUG: Error loading pickup slots:', err);
      console.error('🔍 DEBUG: Error response:', err.response);
      console.error('🔍 DEBUG: Error message:', err.message);
      setError('Failed to load available pickup slots');
    } finally {
      setLoadingSlots(false);
      console.log('🔍 DEBUG: Loading state set to false');
    }
  }, [orderData.assignedParcelDay]);


  // Check BPOINT availability (once, cached)
  // NOTE: This check is completely independent of PaymentSummary.
  // PaymentSummary should work even if BPOINT check fails.
  useEffect(() => {
    const checkBPointAvailability = async () => {
      // Don't re-check if already checked (unless explicitly reset)
      if (bpointAvailable !== null && !bpointLoading) {
        return;
      }
      
      try {
        setBpointLoading(true);
        
        // Calculate total including delivery fee
        const baseTotal = orderData.order?.total_amount || 
                         orderData.orderConfiguration?.totalCalculation?.total || 0;
        const deliveryFeeAmount = parseFloat(deliveryFee) || 0;
        const totalAmount = baseTotal + deliveryFeeAmount;
        
        // Only check if amount > 0
        if (totalAmount <= 0) {
          setBpointAvailable(false);
          setBpointLoading(false);
          return;
        }
        
        console.log('🔍 BPOINT Availability Check:', {
          baseTotal,
          deliveryFee: deliveryFeeAmount,
          totalAmount,
        });
        
        const response = await api.post('/v1/payment/bpoint/check-availability', {
          amount: totalAmount,
        });

        console.log('✅ BPOINT Availability Response:', response.data);

        if (response.data.success) {
          setBpointAvailable(response.data.data.available);
        } else {
          // API returned success: false
          console.warn('⚠️ BPOINT availability check returned false');
          setBpointAvailable(false);
        }
      } catch (error: any) {
        // Completely isolate BPOINT errors - don't let them affect other components
        console.error('❌ Failed to check BPOINT availability:', error);
        
        // Log detailed error for debugging
        if (error.response) {
          console.error('BPOINT Error Response:', {
            status: error.response.status,
            data: error.response.data,
            message: error.response.data?.message || error.message,
          });
        }
        
        // Set BPOINT as unavailable, but don't throw or affect other state
        setBpointAvailable(false);
      } finally {
        setBpointLoading(false);
      }
    };

    // Check if we have either an order total or a configuration total
    const hasTotal = orderData.order?.total_amount || orderData.orderConfiguration?.totalCalculation?.total;
    
    console.log('💳 BPOINT Check - Has Total:', hasTotal, {
      bpointLoading,
      bpointAvailable,
      orderTotal: orderData.order?.total_amount,
      configTotal: orderData.orderConfiguration?.totalCalculation?.total,
      deliveryFee,
    });
    
    // Only check once when we first have a total (bpointAvailable === null means not checked yet)
    if (hasTotal && bpointAvailable === null) {
      // Wrap in additional try-catch to ensure errors are completely isolated
      try {
      checkBPointAvailability();
      } catch (error) {
        // This should never happen since checkBPointAvailability has try-catch,
        // but adding extra safety to ensure complete isolation
        console.error('❌ Unexpected error in BPOINT check (outer catch):', error);
        setBpointAvailable(false);
        setBpointLoading(false);
      }
    } else if (!hasTotal) {
      // No total available yet, mark as not loading but not available
      console.log('⚠️ BPOINT Check - No total available yet');
      setBpointLoading(false);
      setBpointAvailable(false);
    }
    // Note: Removed deliveryFee from dependencies to prevent re-checking on every fee change
  }, [orderData.order?.total_amount, orderData.orderConfiguration?.totalCalculation?.total]);

  // Payment success handler
  const handlePaymentSuccess = async (transactionId: string, receiptNumber: string) => {
    try {
      setPaymentFailedAttempt(false);
      setCardPaymentCompleted(true);
      setPaymentStatus('paid');
      
      // Poll payment summary with exponential backoff
      // This checks for payment updates more efficiently than fixed delays
      const pollPaymentSummary = async (attempt = 1, maxAttempts = 5) => {
        try {
          const url = formik.values.email 
            ? `/v1/payment/orders/${orderData.orderId}/summary?email=${encodeURIComponent(formik.values.email)}`
            : `/v1/payment/orders/${orderData.orderId}/summary`;
          
          const response = await api.get(url);
          
          if (response.data.success) {
            const summary = response.data.data;
            
            // Check if payment has been processed
            // Look for: total_paid updated, payment_status changed, or transaction approved
            const previousTotalPaid = paymentSummary?.total_paid || 0;
            const currentTotalPaid = summary.total_paid || 0;
            
            const paymentDetected = 
              currentTotalPaid > previousTotalPaid || 
              summary.payment_status !== 'pending' ||
              (summary.transactions && summary.transactions.some((t: any) => 
                t.status === 'approved' && t.transaction_id === transactionId
              ));
            
            if (paymentDetected) {
              // Payment processed, update state and stop polling
              console.log('[CheckoutPage] ✅ Payment detected in summary, updating state');
              setPaymentSummary(summary);
      setPaymentSummaryRefreshKey(prev => prev + 1);
              setResetPaymentSelector(true);
              setTimeout(() => setResetPaymentSelector(false), 100);
              return; // Success, stop polling
            }
            
            // If not updated yet, poll again with exponential backoff
            if (attempt < maxAttempts) {
              // Exponential backoff: 1s, 2s, 4s, 5s (max), 5s
              const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
              console.log(`[CheckoutPage] Payment not detected yet, polling again in ${delay}ms (attempt ${attempt + 1}/${maxAttempts})`);
              setTimeout(() => pollPaymentSummary(attempt + 1, maxAttempts), delay);
            } else {
              // Max attempts reached, refresh anyway to show current state
              console.log('[CheckoutPage] Max polling attempts reached, refreshing summary');
              setPaymentSummaryRefreshKey(prev => prev + 1);
            }
          }
        } catch (error) {
          console.error('[CheckoutPage] Failed to poll payment summary:', error);
          // On error, try again with longer delay
          if (attempt < maxAttempts) {
            setTimeout(() => pollPaymentSummary(attempt + 1, maxAttempts), 2000);
          } else {
            // Max attempts reached, refresh anyway
            setPaymentSummaryRefreshKey(prev => prev + 1);
          }
        }
      };
      
      // Start polling after 1 second (faster than previous 1.5s delay)
      setTimeout(() => pollPaymentSummary(), 1000);
      
      // Reset payment selector to "full balance" option
      setResetPaymentSelector(true);
      setTimeout(() => setResetPaymentSelector(false), 100); // Reset the flag after triggering
      
      // Show success message
      setError(`Payment successful! Transaction: ${receiptNumber}. Your payment has been recorded. Please review your order details and click "Place Order" to complete your order.`);
      
      // Note: We don't submit the form yet - allow customer to make additional payments or change delivery method
      // Order will be submitted when customer clicks "Place Order" and accepts terms
    } catch (error) {
      console.error('Payment success handler error:', error);
      setError('Payment was successful, but there was an error updating the order. Please refresh the page.');
    }
  };

  // Payment cancel handler
  const handlePaymentCancel = () => {
    setError('Payment was cancelled. You can try again or select a different payment method.');
  };

  // Payment error handler
  const handlePaymentError = (errorMessage: string) => {
    setPaymentFailedAttempt(true);
    setError(errorMessage || 'Payment failed. Please try again or select a different payment method.');
  };

  // Payment summary loaded handler (memoized to prevent infinite re-renders)
  const handlePaymentSummaryLoaded = useCallback((data: PaymentSummaryType) => {
    setPaymentSummary(data);
    
    // Update card payment completed status based on payment status
    if (data.payment_status === 'fully_paid' || data.payment_status === 'partially_paid') {
      setPaymentFailedAttempt(false);
      setCardPaymentCompleted(true);
    }
    
    // Set default custom amount only on first load so refetches (e.g. after declined payment)
    // don't overwrite the user's chosen amount; keeps Pay button label in sync with selector
    if (data.balance_owing > 0 && !paymentSummaryInitializedRef.current) {
      paymentSummaryInitializedRef.current = true;
      setCustomPaymentAmount(data.balance_owing);
    }
  }, []);

  // Payment amount changed handler (memoized to prevent unnecessary re-renders)
  const handlePaymentAmountChange = useCallback((amount: number, isCustom: boolean) => {
    setCustomPaymentAmount(amount);
    setIsCustomPayment(isCustom);
    console.log('Payment amount changed:', { amount, isCustom });
  }, []);

  const formik = useFormik<CheckoutFormData>({
    initialValues: {
      // Initialize with existing customer details (from customer details step)
      firstName: orderData.customerDetails?.firstName || '',
      lastName: orderData.customerDetails?.lastName || '',
      email: orderData.customerDetails?.email || '',
      phone: orderData.customerDetails?.phone || '',
      address: orderData.customerDetails?.address || '',
      suburb: orderData.customerDetails?.suburb || '',
      postcode: orderData.customerDetails?.postcode || '',
      notes: orderData.customerDetails?.notes || '',
      
      // Use delivery method from customer details so fee is calculated on first load when they chose delivery
      deliveryMethod: (orderData.customerDetails?.delivery_method === 'delivery' ? 'delivery' : 'pickup') as 'pickup' | 'delivery',
      paymentMethod: 'direct_deposit',
      voucherAmount: '',
      customerComment: (orderData.order as { customer_comment?: string } | undefined)?.customer_comment ?? '',
      termsAccepted: false,
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required('First name is required'),
      lastName: Yup.string().required('Last name is required'),
      email: Yup.string().email('Invalid email address').required('Email is required'),
      phone: Yup.string().required('Phone number is required'),
      address: Yup.string().when('deliveryMethod', {
        is: 'delivery',
        then: (schema) => schema.required('Address is required for delivery'),
        otherwise: (schema) => schema.optional(),
      }),
      suburb: Yup.string().when('deliveryMethod', {
        is: 'delivery',
        then: (schema) => schema.required('Suburb is required for delivery'),
        otherwise: (schema) => schema.optional(),
      }),
      postcode: Yup.string().when('deliveryMethod', {
        is: 'delivery',
        then: (schema) => schema.required('Postcode is required for delivery'),
        otherwise: (schema) => schema.optional(),
      }),
      deliveryMethod: Yup.string().oneOf(['pickup', 'delivery']).required('Please select delivery method'),
      pickupSlotId: Yup.mixed().when('deliveryMethod', {
        is: 'pickup',
        then: (schema) => schema.required('Please select a pickup time slot'),
        otherwise: (schema) => schema.optional(),
      }),
      paymentMethod: Yup.string().oneOf(['direct_deposit', 'card_payment']).required('Please select payment method'),
      voucherAmount: Yup.string().test('voucher-amount', 'Voucher amount must be a valid number', function(value) {
        if (!value || value.trim() === '') return true; // Optional field
        const numericValue = parseFloat(value.replace(/[$,]/g, ''));
        return !isNaN(numericValue) && numericValue >= 0;
      }),
      customerComment: Yup.string().max(5000, 'Comment must be at most 5000 characters'),
      termsAccepted: Yup.boolean().oneOf([true], 'You must accept the terms and conditions'),
    }).test('delivery-address-validation', 'Delivery address validation failed', async function(value) {
      if (value.deliveryMethod !== 'delivery') {
        return true; // Skip validation for pickup
      }
      
      if (!value.postcode || !value.suburb) {
        return this.createError({ message: 'Postcode and suburb are required for delivery' });
      }
      
      const validation = await validateDeliveryAddress(value.postcode, value.suburb);
      if (!validation.isValid) {
        return this.createError({ message: validation.message });
      }
      
      return true;
    }),
    onSubmit: async (values: CheckoutFormData, { setSubmitting }: FormikHelpers<CheckoutFormData>) => {
      setLoading(true);
      setError(null);
      
      try {
        // Parcel day is required for submit; backend will return a clear error if missing
        const parcelDayId = orderData.assignedParcelDay?.id ?? null;
        if (!parcelDayId) {
          setError('Delivery/pickup date is required. Please go back to the previous step and ensure a date is selected.');
          return;
        }

        const checkoutData = {
          // Customer details
          customer_details: {
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            phone: values.phone,
            address: values.address,
            suburb: values.suburb,
            postcode: values.postcode,
            notes: values.notes,
          },
          
          // Delivery details – always send parcel_day_id; for delivery, clear pickup slot
          delivery_method: values.deliveryMethod,
          parcel_day_id: parcelDayId,
          parcel_day_pickup_slot_id: values.deliveryMethod === 'delivery' ? null : (values.pickupSlotId ?? null),
          delivery_fee: deliveryFeeAmount,
          
          // Payment details – order total before voucher = items + delivery
          payment_method: voucherAmount > 0 && voucherAmount >= (orderItemsTotal + deliveryFeeAmount) ? 'voucher' :
                         voucherAmount > 0 ? `voucher + ${values.paymentMethod}` : values.paymentMethod,
          voucher_amount: voucherAmount,
          customer_comment: values.customerComment?.trim() || null,
          terms_accepted: values.termsAccepted,
        };
        
        await onSubmit(checkoutData);
      } catch (err: any) {
        // No HTTP response (network, CORS, wrong URL) – show actionable message
        if (!err.response) {
          setError('Connection or server error. Check your connection and the browser Network tab, then try again.');
        } else {
          const data = err.response.data;
          const msg = data?.message || 'Failed to submit order';
          const backendError = data?.error;
          const hint = data?.hint;
          if (data?.message || backendError) {
            setError(backendError ? `${msg} ${typeof backendError === 'string' ? backendError : ''}`.trim() : msg);
          } else {
            const status = err.response.status;
            setError(status ? `Server error (HTTP ${status}). Please try again or contact support.` : 'Failed to submit order.');
          }
          if (hint && typeof hint === 'string') {
            console.warn('[CheckoutPage] Submit error hint:', hint);
          }
        }
      } finally {
        setLoading(false);
        setSubmitting(false);
      }
    },
  });

  // Keep refs in sync so async delivery-fee callback can ignore stale responses
  deliveryMethodRef.current = formik.values.deliveryMethod;
  postcodeRef.current = formik.values.postcode ?? '';

  // Keep customer email ref in sync for payment verification (message handler uses it)
  useEffect(() => {
    customerEmailRef.current = formik.values.email ?? '';
  }, [formik.values.email]);

  // Validate delivery address when postcode or suburb changes (regardless of delivery method)
  useEffect(() => {
    if (formik.values.postcode && formik.values.suburb) {
      const timeoutId = setTimeout(() => {
        validateDeliveryAddress(formik.values.postcode, formik.values.suburb);
      }, 800); // Increased from 500ms for slower typers

      return () => clearTimeout(timeoutId);
    } else {
      setDeliveryZoneValidation(null);
    }
  }, [formik.values.postcode, formik.values.suburb]);

  // Load available pickup slots when parcel day changes or delivery method changes to pickup
  useEffect(() => {
    console.log('🔍 DEBUG: useEffect triggered for pickup slots');
    console.log('🔍 DEBUG: orderData.assignedParcelDay:', orderData.assignedParcelDay);
    console.log('🔍 DEBUG: pickup_schedule?.id:', orderData.assignedParcelDay?.pickup_schedule?.id);
    console.log('🔍 DEBUG: deliveryMethod:', formik.values.deliveryMethod);
    
    if (orderData.assignedParcelDay?.pickup_schedule?.id && formik.values.deliveryMethod === 'pickup') {
      console.log('🔍 DEBUG: Calling loadAvailablePickupSlots from useEffect');
      loadAvailablePickupSlots();
    } else {
      console.log('🔍 DEBUG: No pickup schedule ID or not pickup method, not loading slots');
    }
  }, [orderData.assignedParcelDay, formik.values.deliveryMethod, loadAvailablePickupSlots]);

  // Clear pickup slots when switching to delivery
  useEffect(() => {
    if (formik.values.deliveryMethod === 'delivery') {
      setAvailablePickupSlots([]);
      setLoadingSlots(false);
    }
  }, [formik.values.deliveryMethod]);

  // Calculate delivery fee when delivery method or postcode changes.
  // Set loading true synchronously so UI updates immediately when toggling to delivery.
  useEffect(() => {
    if (formik.values.deliveryMethod === 'delivery' && formik.values.postcode) {
      setDeliveryFeeLoading(true);
      calculateDeliveryFee(formik.values.postcode);
    } else {
      setDeliveryFee(0);
      setDeliveryFeeLoading(false);
    }
  }, [formik.values.deliveryMethod, formik.values.postcode]);

  // Clear validation when postcode or suburb changes so we don't apply stale validation
  // (otherwise we'd switch to pickup based on the previous address and clear the delivery fee)
  useEffect(() => {
    setDeliveryZoneValidation(null);
  }, [formik.values.postcode, formik.values.suburb]);

  // Check if address fields are empty or invalid and update delivery option state
  useEffect(() => {
    const hasAddress = formik.values.address && formik.values.suburb && formik.values.postcode;
    const isAddressValid = deliveryZoneValidation?.isValid === true;
    
    if (!hasAddress) {
      setDeliveryOptionDisabled(true);
      setDeliveryDisabledMessage('Add your address to enable delivery');
      // If delivery is currently selected but no address, switch to pickup
      if (formik.values.deliveryMethod === 'delivery') {
        formik.setFieldValue('deliveryMethod', 'pickup');
      }
    } else if (hasAddress && deliveryZoneValidation && !isAddressValid) {
      setDeliveryOptionDisabled(true);
      setDeliveryDisabledMessage(deliveryZoneValidation.message || 'Invalid delivery address');
      // If delivery is currently selected but address is invalid, switch to pickup
      if (formik.values.deliveryMethod === 'delivery') {
        formik.setFieldValue('deliveryMethod', 'pickup');
      }
    } else if (hasAddress && isAddressValid) {
      setDeliveryOptionDisabled(false);
      setDeliveryDisabledMessage('');
    } else {
      // Address is provided but validation hasn't completed yet
      setDeliveryOptionDisabled(true);
      setDeliveryDisabledMessage('Validating address...');
    }
  }, [formik.values.address, formik.values.suburb, formik.values.postcode, deliveryZoneValidation]);

  // Reset card payment completion when payment method changes
  useEffect(() => {
    setCardPaymentCompleted(false);
  }, [formik.values.paymentMethod]);

  // Set card payment as default when BPOINT becomes available
  useEffect(() => {
    if (bpointAvailable && !bpointLoading && formik.values.paymentMethod === 'direct_deposit') {
      formik.setFieldValue('paymentMethod', 'card_payment');
    }
  }, [bpointAvailable, bpointLoading]);

  // Auto-select delivery when address becomes valid
  useEffect(() => {
    if (!deliveryOptionDisabled && 
        deliveryZoneValidation?.isValid && 
        formik.values.deliveryMethod === 'pickup') {
      formik.setFieldValue('deliveryMethod', 'delivery');
    }
  }, [deliveryZoneValidation?.isValid, deliveryOptionDisabled]);

  const formatDate = (dateString: string) => formatCalendarDate(dateString);
  const formatTime = (timeString: string) => formatTimeOfDay(timeString);

  const validateDeliveryAddress = async (postcode: string, suburb: string) => {
    if (!postcode || !suburb) {
      setDeliveryZoneValidation(null);
      return { isValid: true, message: '' };
    }

    setValidatingPostcode(true);
    
    try {
      const validation = await deliveryZoneService.validateDeliveryAddress(postcode, suburb);
      
      setDeliveryZoneValidation({
        isValid: validation.is_valid,
        message: validation.message,
        zone: validation.zone
      });
      // Use fee from validation response so delivery fee is set even when calculateDeliveryFee uses a different code path
      if (validation.is_valid && validation.fee != null) {
        setDeliveryFee(Number(validation.fee));
      }
      
      return { 
        isValid: validation.is_valid, 
        message: validation.message, 
        zone: validation.zone 
      };
      
    } catch (error) {
      console.warn('Error validating delivery address:', error);
      setDeliveryZoneValidation({
        isValid: false,
        message: 'Unable to validate delivery address. Please try again or contact us for assistance.'
      });
      return { isValid: false, message: 'Validation error' };
    } finally {
      setValidatingPostcode(false);
    }
  };

  const calculateDeliveryFee = async (requestPostcode: string) => {
    if (!requestPostcode) {
      setDeliveryFeeLoading(false);
      setDeliveryFee(0);
      return;
    }
    try {
      const fee = await deliveryZoneService.calculateDeliveryFee(requestPostcode);
      if (deliveryMethodRef.current === 'delivery' && postcodeRef.current === requestPostcode) {
        setDeliveryFee(fee);
      }
    } catch (error) {
      console.warn('Could not calculate delivery fee:', error);
      if (deliveryMethodRef.current === 'delivery' && postcodeRef.current === requestPostcode) {
        setDeliveryFee(0);
      }
    } finally {
      setDeliveryFeeLoading(false);
    }
  };

  // Build order summary from backend order data
  const buildOrderSummary = () => {
    console.log('CheckoutPage - buildOrderSummary called');
    console.log('CheckoutPage - orderData:', orderData);
    console.log('CheckoutPage - orderData.order:', orderData.order);
    console.log('CheckoutPage - orderData.orderConfiguration:', orderData.orderConfiguration);

    // Guard: session expired or missing food parcel (e.g. after long idle) — avoid reading .price on undefined
    const fp = orderData?.foodParcel;
    if (!fp) {
      return {
        foodParcel: { name: '—', price: 0 },
        bundles: { total: 0, count: 0, items: [] as any[] },
        bread: null as any,
        grocery: { total: 0, items: [] as any[] },
        parcel_items: { total: 0, items: [] as any[] },
        addon_products: { total: 0, items: [] as any[] },
        total: 0,
      };
    }

    // First try to use order data if available
    if (orderData.order && orderData.order.orderItems && Array.isArray(orderData.order.orderItems)) {
      console.log('CheckoutPage - Using order data for summary');
      const summary = {
        foodParcel: { 
          name: orderData.order.foodParcel?.name ?? fp.name, 
          price: orderData.order.foodParcel?.price ?? fp.price 
        },
        bundles: { total: 0, count: 0, items: [] as any[] },
        bread: null as any,
        grocery: { total: 0, items: [] as any[] },
        parcel_items: { total: 0, items: [] as any[] },
        addon_products: { total: 0, items: [] as any[] },
        total: orderData.order.total_amount ?? orderData.totalAmount ?? fp.price
      };

      // Group order items by type and combine quantities
      console.log('CheckoutPage - Processing order items:', orderData.order.orderItems);
      
      // Group bundle items by bundle and combine quantities
      const bundleItems = new Map();
      const groceryItems = new Map();
      const addonItems = new Map();
      const addonProductItems = new Map<string, { name: string; quantity: number; unit_price: number; line_total: number }>();
      
      orderData.order.orderItems.forEach(item => {
        console.log('CheckoutPage - Processing item:', item);
        
        switch (item.item_type) {
          case 'bundle':
            summary.bundles.total += item.total_price;
            summary.bundles.count++;
            
            // Group bundle items by bundle ID and combine quantities
            const bundleId = item.productBundle?.id;
            const bundleName = item.productBundle?.name || `Bundle ${bundleId}`;
            
            if (bundleId && bundleItems.has(bundleId)) {
              const existing = bundleItems.get(bundleId);
              existing.quantity += item.quantity;
              existing.totalPrice += item.total_price;
            } else if (bundleId) {
              bundleItems.set(bundleId, {
                id: bundleId,
                name: bundleName,
                quantity: item.quantity,
                totalPrice: item.total_price,
                unitPrice: item.unit_price
              });
            }
            break;
            
          case 'bread':
            summary.bread = {
              name: item.product?.name || 'Bread Selection',
              price: item.total_price
            };
            break;
            
          case 'grocery':
            summary.grocery.total += item.total_price;
            
            // Group grocery items by product ID and combine quantities
            const groceryId = item.product?.id;
            const groceryName = item.product?.name || `Grocery Item ${groceryId}`;
            
            if (groceryId && groceryItems.has(groceryId)) {
              const existing = groceryItems.get(groceryId);
              existing.quantity += item.quantity;
              existing.totalPrice += item.total_price;
            } else if (groceryId) {
              groceryItems.set(groceryId, {
                id: groceryId,
                name: groceryName,
                quantity: item.quantity,
                totalPrice: item.total_price,
                unitPrice: item.unit_price
              });
            }
            break;
            
          case 'addon':
            summary.parcel_items.total += item.total_price;
            
            // Group addon items (optional items) by selection reference and combine quantities
            const addonKey = item.selection_reference;
            const addonName = item.selection_reference.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            
            if (addonItems.has(addonKey)) {
              const existing = addonItems.get(addonKey);
              existing.quantity += item.quantity;
              existing.totalPrice += item.total_price;
            } else {
              addonItems.set(addonKey, {
                key: addonKey,
                name: addonName,
                quantity: item.quantity,
                totalPrice: item.total_price,
                unitPrice: item.unit_price
              });
            }
            break;

          case 'addon_product':
            summary.addon_products.total += item.total_price;
            const productName = item.product?.name || item.product_snapshot?.name || `Product ${item.selection_reference || ''}`;
            const prodKey = item.selection_reference || `product_${item.product?.id ?? 'unknown'}`;
            if (addonProductItems.has(prodKey)) {
              const existing = addonProductItems.get(prodKey)!;
              existing.quantity += item.quantity;
              existing.line_total += item.total_price;
            } else {
              addonProductItems.set(prodKey, {
                name: productName,
                quantity: item.quantity,
                unit_price: item.unit_price,
                line_total: item.total_price
              });
            }
            break;
        }
      });
      
      // Convert maps to arrays for the summary
      summary.bundles.items = Array.from(bundleItems.values());
      summary.grocery.items = Array.from(groceryItems.values());
      summary.parcel_items.items = Array.from(addonItems.values());
      summary.addon_products.items = Array.from(addonProductItems.values());

      console.log('CheckoutPage - Final summary from order data:', summary);
      return summary;
    }
    
    // Fallback to orderConfiguration data if available
    if (orderData.orderConfiguration && orderData.orderConfiguration.totalCalculation) {
      console.log('CheckoutPage - Using orderConfiguration data for summary');
      const config = orderData.orderConfiguration;
      const breakdown = config.totalCalculation.breakdown;
      
      const summary = {
        foodParcel: { 
          name: fp.name, 
          price: fp.price 
        },
        bundles: { 
          total: breakdown.bundles?.total || 0, 
          count: breakdown.bundles?.count || 0, 
          items: [] as any[] 
        },
        bread: breakdown.bread ? {
          name: breakdown.bread.name,
          price: breakdown.bread.price
        } : null,
        grocery: { 
          total: breakdown.grocery?.total || 0, 
          items: breakdown.grocery?.items || [] 
        },
        parcel_items: { 
          total: breakdown.parcel_items?.total || 0, 
          items: breakdown.parcel_items?.items || [] 
        },
        addon_products: { 
          total: breakdown.addon_products?.total || 0, 
          items: breakdown.addon_products?.items || [] 
        },
        total: config.totalCalculation.total ?? orderData.totalAmount ?? fp.price
      };

      // For orderConfiguration data, we need to create proper item structures
      // since the breakdown doesn't include quantities, we'll assume quantity of 1 for each item
      if (breakdown.grocery?.items) {
        console.log('CheckoutPage - Processing grocery items:', breakdown.grocery.items);
        
        // Group grocery items by name and combine quantities
        const groceryMap = new Map();
        breakdown.grocery.items.forEach((item: any) => {
          const price = typeof item.price === 'string' ? parseFloat(item.price) : item.price;
          console.log('CheckoutPage - Grocery item:', item.name, 'price:', price);
          
          if (groceryMap.has(item.name)) {
            const existing = groceryMap.get(item.name);
            existing.quantity += 1;
            existing.totalPrice += price;
          } else {
            groceryMap.set(item.name, {
              id: groceryMap.size + 1,
              name: item.name,
              quantity: 1,
              totalPrice: price,
              unitPrice: price,
              price: price
            });
          }
        });
        
        summary.grocery.items = Array.from(groceryMap.values());
      }

      if (breakdown.parcel_items?.items) {
        console.log('CheckoutPage - Processing parcel items (Optional Items):', breakdown.parcel_items.items);
        summary.parcel_items.items = breakdown.parcel_items.items.map((item: any, index: number) => {
          const price = typeof item.price === 'string' ? parseFloat(item.price) : item.price;
          return {
            key: `optional_${index + 1}`,
            name: item.name,
            quantity: 1,
            totalPrice: price,
            unitPrice: price,
            price: price
          };
        });
      }

      if (breakdown.addon_products?.items) {
        console.log('CheckoutPage - Processing addon products:', breakdown.addon_products.items);
        summary.addon_products.items = breakdown.addon_products.items.map((item: any) => ({
          name: item.name,
          quantity: item.quantity ?? 1,
          unit_price: item.unit_price ?? 0,
          line_total: item.line_total ?? (item.quantity ?? 1) * (item.unit_price ?? 0)
        }));
      }

      // For bundles, use breakdown.bundles.items when available (includes names from order config)
      if (config.configuration.bundle_selections && breakdown.bundles?.count && breakdown.bundles.count > 0) {
        if (breakdown.bundles.items && breakdown.bundles.items.length > 0) {
          // Group by bundle id so "3x Same Bundle Name $42" shows as one line
          const byId = new Map<number, { id: number; name: string; quantity: number; totalPrice: number; unitPrice: number }>();
          breakdown.bundles.items.forEach((b) => {
            const price = typeof b.price === 'number' ? b.price : (Number(b.price) || 0);
            if (byId.has(b.id)) {
              const existing = byId.get(b.id)!;
              existing.quantity += 1;
              existing.totalPrice += price;
            } else {
              byId.set(b.id, {
                id: b.id,
                name: b.name,
                quantity: 1,
                totalPrice: price,
                unitPrice: price,
              });
            }
          });
          summary.bundles.items = Array.from(byId.values());
        } else {
          // Fallback when breakdown has no items (e.g. older cached data): use IDs and total/count
          const bundleSelections = config.configuration.bundle_selections.filter((id: number | null): id is number => id !== null);
          const bundlePrice = breakdown.bundles ? breakdown.bundles.total / breakdown.bundles.count : 0;
          summary.bundles.items = bundleSelections.map((bundleId: number) => ({
            id: bundleId,
            name: `Bundle #${bundleId}`,
            quantity: 1,
            totalPrice: bundlePrice,
            unitPrice: bundlePrice,
          }));
        }
      }

      console.log('CheckoutPage - Final summary from orderConfiguration:', summary);
      return summary;
    }
    
    // Final fallback - just food parcel
    console.log('CheckoutPage - Using fallback summary (food parcel only)');
    return {
      foodParcel: { name: fp.name, price: fp.price },
      bundles: { total: 0, count: 0, items: [] as any[] },
      bread: null as any,
      grocery: { total: 0, items: [] as any[] },
      parcel_items: { total: 0, items: [] as any[] },
      addon_products: { total: 0, items: [] as any[] },
      total: orderData.totalAmount ?? fp.price
    };
  };

  const orderSummary = buildOrderSummary();

  /**
   * CENTRALIZED TOTAL CALCULATION - Single Source of Truth (Backend)
   * 
   * We use backend-calculated totals to ensure consistency across the entire application.
   * Priority order:
   * 1. Payment Summary (most accurate, includes real-time calculation)
   * 2. Order Total Amount (from database)
   * 3. Fallback to 0 if neither is available
   */
  
  /**
   * ============================================================================
   * CENTRALIZED CALCULATION - SINGLE SOURCE OF TRUTH
   * ============================================================================
   * 
   * These calculations are used throughout the entire checkout page to ensure
   * consistency across all components (Order Summary, Payment Summary, etc.)
   * 
   * Priority order for each value:
   * 1. User's current input (formik.values) - LIVE
   * 2. Backend cached value (paymentSummary) - FALLBACK
   * 3. Default value - LAST RESORT
   */
  
  // 1. Order Items Total (excludes delivery fee and vouchers)
  const orderItemsTotal = paymentSummary?.total_amount ?? 
                     (orderData.order?.total_amount ? parseFloat(orderData.order.total_amount) : 0);
  
  // 2. Delivery Fee - Use formik as primary source (user may have changed delivery method)
  const deliveryFeeAmount = (parseFloat(deliveryFee) || 0) || (paymentSummary?.delivery_fee ?? 0);
  
  // 3. Voucher Amount - CRITICAL: Use user input FIRST, backend as fallback
  const voucherAmount = formik.values.voucherAmount 
    ? (parseFloat(formik.values.voucherAmount.replace(/[$,]/g, '')) || 0) 
    : (paymentSummary?.voucher_amount ?? 0);
  
  // 4. Total Paid (from approved payment transactions)
  const totalPaid = paymentSummary?.total_paid ?? 0;
  
  /**
   * KEY CALCULATION #1: TOTAL AMOUNT PAYABLE
   * This is the complete order cost before any payments
   * Formula: Order Items + Delivery Fee - Vouchers
   */
  const totalAmountPayable = Math.max(0, orderItemsTotal + deliveryFeeAmount - voucherAmount);
  
  /**
   * KEY CALCULATION #2: BALANCE OWING (Live)
   * This is what the customer still owes after payments
   * Formula: Total Amount Payable - Payments Made
   * Round to 2 decimals to avoid floating-point showing "Payment Required" with $0.00.
   */
  const balanceOwing = Math.round(Math.max(0, totalAmountPayable - totalPaid) * 100) / 100;
  
  // Helper: Order total after voucher (for Order Summary breakdown display)
  const orderTotalAfterVoucher = Math.max(0, orderItemsTotal - voucherAmount);
  
  /**
   * ============================================================================
   * END CENTRALIZED CALCULATION
   * ============================================================================
   */

  // Show loading state if order data is not available
  // if (loadingOrderData || (!orderData.order && orderData.orderId)) {
  //   return (
  //     <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
  //       <CircularProgress />
  //       <Typography sx={{ ml: 2 }}>Loading order details...</Typography>
  //     </Box>
  //   );
  // }

  // Session expired or order data missing (e.g. long idle) — show friendly message instead of form
  if (!orderData?.foodParcel) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Checkout
        </Typography>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Session expired or order data missing</AlertTitle>
          Your order session may have expired. Please start again from the Food Parcels page to place your order.
        </Alert>
        <Button variant="contained" onClick={onBack}>
          Back to order
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Checkout
      </Typography>
      
      {verifyingPayment && (
        <Alert severity="info" icon={<CircularProgress size={20} />} sx={{ mb: 2 }}>
          Verifying payment…
        </Alert>
      )}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={3}>
          {/* Order Summary */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Order Summary
              </Typography>
              
              <List>
                {/* Food Parcel with Bundles */}
                <ListItem>
                  <ListItemText
                    primary="Food Parcel"
                    secondary={orderSummary.foodParcel.name}
                  />
                  <Typography variant="body1">
                    ${Number(orderSummary.foodParcel.price).toFixed(2)}
                  </Typography>
                </ListItem>
                
                {/* Bundle Selections within Food Parcel */}
                {orderSummary.bundles.count > 0 && (
                  <>
                    {orderSummary.bundles.items.map((item) => (
                      <ListItem key={`bundle-${item.id}`} sx={{ pl: 4 }}>
                        <ListItemText
                          primary={`${item.quantity || 1}x ${item.name}`}
                        />
                        <Typography variant="body2" color="text.secondary">
                          ${Number(item.totalPrice || 0).toFixed(2)}
                        </Typography>
                      </ListItem>
                    ))}
                  </>
                )}
                <Divider />

                {/* Bread Selection */}
                {orderSummary.bread && (
                  <>
                    <ListItem>
                      <ListItemText
                        primary="Bread Selection"
                        secondary={orderSummary.bread.name}
                      />
                      <Typography variant="body1">
                        ${Number(orderSummary.bread.price).toFixed(2)}
                      </Typography>
                    </ListItem>
                    <Divider />
                  </>
                )}

                {/* Grocery Items - Detailed */}
                {orderSummary.grocery.total > 0 && (
                  <>
                    <ListItem>
                      <ListItemText
                        primary="Grocery Items"
                        secondary={`${orderSummary.grocery.items.length} items selected`}
                      />
                      <Typography variant="body1" fontWeight="bold">
                        ${Number(orderSummary.grocery.total).toFixed(2)}
                      </Typography>
                    </ListItem>
                    {orderSummary.grocery.items.map((item, index) => (
                      <ListItem key={`grocery-${item.id || index}`} sx={{ pl: 4 }}>
                        <ListItemText
                          primary={`${item.quantity || 1}x ${item.name}`}
                        />
                        <Typography variant="body2" color="text.secondary">
                          ${Number(item.totalPrice || item.price || 0).toFixed(2)}
                        </Typography>
                      </ListItem>
                    ))}
                    <Divider />
                  </>
                )}

                {/* Optional Items (parcel_items) */}
                {orderSummary.parcel_items.total > 0 && (
                  <>
                    <ListItem>
                      <ListItemText
                        primary="Optional Items"
                        secondary={`${orderSummary.parcel_items.items.length} items selected`}
                      />
                      <Typography variant="body1" fontWeight="bold">
                        ${Number(orderSummary.parcel_items.total).toFixed(2)}
                      </Typography>
                    </ListItem>
                    {orderSummary.parcel_items.items.map((item, index) => (
                      <ListItem key={`optional-${item.key || index}`} sx={{ pl: 4 }}>
                        <ListItemText
                          primary={`${item.quantity || 1}x ${item.name}`}
                        />
                        <Typography variant="body2" color="text.secondary">
                          ${Number(item.totalPrice || item.price || 0).toFixed(2)}
                        </Typography>
                      </ListItem>
                    ))}
                    <Divider />
                  </>
                )}

                {/* Add-ons (products from product list) */}
                {orderSummary.addon_products && orderSummary.addon_products.total > 0 && orderSummary.addon_products.items.length > 0 && (
                  <>
                    <ListItem>
                      <ListItemText
                        primary="Add-ons"
                        secondary={`${orderSummary.addon_products.items.length} product(s) selected`}
                      />
                      <Typography variant="body1" fontWeight="bold">
                        ${Number(orderSummary.addon_products.total).toFixed(2)}
                      </Typography>
                    </ListItem>
                    {orderSummary.addon_products.items.map((item: any, index: number) => (
                      <ListItem key={`addon-${index}`} sx={{ pl: 4 }}>
                        <ListItemText
                          primary={`${item.quantity || 1}x ${item.name}`}
                        />
                        <Typography variant="body2" color="text.secondary">
                          ${Number(item.line_total ?? item.unit_price * (item.quantity || 1)).toFixed(2)}
                        </Typography>
                      </ListItem>
                    ))}
                    <Divider />
                  </>
                )}

                {/* Total Order Amount */}
                    <Divider />
                <ListItem>
                  <ListItemText
                    primary="Total Order Amount"
                    primaryTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
                    secondary="Items only (see Payment Summary for delivery, vouchers, and payments)"
                  />
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    ${orderItemsTotal.toFixed(2)}
                  </Typography>
                </ListItem>
              </List>
            </Paper>

            {/* Order comment */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Order comment (optional)
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={3}
                maxRows={8}
                id="customerComment"
                name="customerComment"
                placeholder="Add any notes or special requests for your order…"
                value={formik.values.customerComment}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.customerComment && Boolean(formik.errors.customerComment)}
                helperText={
                  formik.touched.customerComment && formik.errors.customerComment
                    ? formik.errors.customerComment
                    : `${5000 - (formik.values.customerComment?.length ?? 0)} characters remaining`
                }
                inputProps={{ maxLength: 5000 }}
              />
            </Paper>

            {/* Parcel Day Information */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Pickup/Delivery Date
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {orderData.assignedParcelDay ? formatDate(orderData.assignedParcelDay.date) : 'No date assigned'}
              </Typography>
              {orderData.assignedParcelDay?.order_time && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Order Time: {orderData.assignedParcelDay.order_time.start_day} {formatTime(orderData.assignedParcelDay.order_time.start_time)} - {orderData.assignedParcelDay.order_time.end_day} {formatTime(orderData.assignedParcelDay.order_time.end_time)}
                </Typography>
              )}
              
              {/* Delivery Method Selection */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Delivery Method
                </Typography>
                <FormControl component="fieldset" fullWidth>
                  <RadioGroup
                    name="deliveryMethod"
                    value={formik.values.deliveryMethod}
                    onChange={(e) => {
                      formik.handleChange(e);
                      // Manually trigger pickup slots loading when switching to pickup
                      if (e.target.value === 'pickup' && orderData.assignedParcelDay?.pickup_schedule?.id) {
                        setTimeout(() => {
                          loadAvailablePickupSlots();
                        }, 100);
                      }
                    }}
                  >
                    <FormControlLabel
                      value="pickup"
                      control={<Radio />}
                      label="Pickup"
                    />
                    <FormControlLabel
                      value="delivery"
                      control={<Radio disabled={deliveryOptionDisabled} />}
                      label={
                        <Box>
                          <Typography component="span">Delivery</Typography>
                          {deliveryOptionDisabled && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {deliveryDisabledMessage}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </RadioGroup>
                </FormControl>

                {/* Pickup Time Slots */}
                {formik.values.deliveryMethod === 'pickup' && orderData.assignedParcelDay?.pickup_schedule && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Select Pickup Time
                    </Typography>
                    {(() => {
                      console.log('🔍 DEBUG: Rendering pickup slots section');
                      console.log('🔍 DEBUG: loadingSlots:', loadingSlots);
                      console.log('🔍 DEBUG: availablePickupSlots:', availablePickupSlots);
                      console.log('🔍 DEBUG: availablePickupSlots.length:', availablePickupSlots.length);
                      console.log('🔍 DEBUG: pickup_schedule.id:', orderData.assignedParcelDay?.pickup_schedule?.id);
                      
                      return null;
                    })()}
                    {loadingSlots ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={20} />
                        <Typography variant="body2" color="text.secondary">
                          Loading pickup slots...
                        </Typography>
                      </Box>
                    ) : availablePickupSlots.length > 0 ? (
                      <FormControl fullWidth>
                        <Select
                          name="pickupSlotId"
                          value={formik.values.pickupSlotId || ''}
                          onChange={formik.handleChange}
                          displayEmpty
                          error={formik.touched.pickupSlotId && Boolean(formik.errors.pickupSlotId)}
                        >
                          <MenuItem value="" disabled>
                            <em>Select a pickup time</em>
                          </MenuItem>
                          {availablePickupSlots.map((slot) => {
                            const formattedStart = formatTime(slot.start_time);
                            const formattedEnd = formatTime(slot.end_time);
                            const displayText = `${formattedStart} - ${formattedEnd} (${slot.available_capacity} slots available)`;
                            
                            console.log('🔍 DEBUG: Rendering slot:', {
                              slot_id: slot.id,
                              start_time: slot.start_time,
                              end_time: slot.end_time,
                              formatted_start: formattedStart,
                              formatted_end: formattedEnd,
                              available_capacity: slot.available_capacity,
                              display_text: displayText
                            });
                            
                            return (
                              <MenuItem key={slot.id} value={slot.id}>
                                {displayText}
                              </MenuItem>
                            );
                          })}
                        </Select>
                        {formik.touched.pickupSlotId && formik.errors.pickupSlotId && (
                          <FormHelperText error>{formik.errors.pickupSlotId}</FormHelperText>
                        )}
                      </FormControl>
                    ) : !loadingSlots && availablePickupSlots.length === 0 ? (
                      <Alert severity="warning">
                        No pickup slots available for this date.
                      </Alert>
                    ) : (
                      <Alert severity="info">
                        Pickup slots will be loaded when you select pickup.
                      </Alert>
                    )}
                  </Box>
                )}

                {/* Delivery Information - show fee or loading as soon as delivery is selected */}
                {formik.values.deliveryMethod === 'delivery' && (
                  <Box sx={{ mt: 2 }}>
                    <Alert severity="info">
                      Delivery will be made between 9:00 AM and 1:00 PM on the selected date.
                      {(deliveryFeeLoading || deliveryFee > 0) && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {deliveryFeeLoading ? 'Loading delivery fee…' : `Delivery fee: $${deliveryFee.toFixed(2)}`}
                        </Typography>
                      )}
                    </Alert>
                  </Box>
                )}

                {/* Address Validation Status (always show when address is provided) */}
                {formik.values.address && formik.values.suburb && formik.values.postcode && (
                  <Box sx={{ mt: 2 }}>
                    {validatingPostcode && (
                      <Alert severity="info">
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <CircularProgress size={16} sx={{ mr: 1 }} />
                          Validating delivery address...
                        </Box>
                      </Alert>
                    )}
                    {deliveryZoneValidation && !validatingPostcode && !deliveryZoneValidation.isValid && (
                      <Alert severity="error">
                        {deliveryZoneValidation.message}
                      </Alert>
                    )}
                    {deliveryZoneValidation && !validatingPostcode && deliveryZoneValidation.isValid && (
                      <Alert severity="success">
                        {deliveryZoneValidation.message}
                      </Alert>
                    )}
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Checkout Form */}
          <Grid item xs={12} md={4}>
            {/* Customer Details */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Customer Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    id="firstName"
                    name="firstName"
                    label="First Name"
                    value={formik.values.firstName}
                    onChange={formik.handleChange}
                    error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                    helperText={formik.touched.firstName && formik.errors.firstName}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    id="lastName"
                    name="lastName"
                    label="Last Name"
                    value={formik.values.lastName}
                    onChange={formik.handleChange}
                    error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                    helperText={formik.touched.lastName && formik.errors.lastName}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    id="email"
                    name="email"
                    label="Email"
                    type="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    helperText={formik.touched.email && formik.errors.email}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    id="phone"
                    name="phone"
                    label="Phone"
                    value={formik.values.phone}
                    onChange={formik.handleChange}
                    error={formik.touched.phone && Boolean(formik.errors.phone)}
                    helperText={formik.touched.phone && formik.errors.phone}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required={formik.values.deliveryMethod === 'delivery'}
                    id="address"
                    name="address"
                    label="Address"
                    value={formik.values.address}
                    onChange={formik.handleChange}
                    error={formik.touched.address && Boolean(formik.errors.address)}
                    helperText={formik.touched.address && formik.errors.address}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required={formik.values.deliveryMethod === 'delivery'}
                    id="suburb"
                    name="suburb"
                    label="Suburb"
                    value={formik.values.suburb}
                    onChange={formik.handleChange}
                    error={formik.touched.suburb && Boolean(formik.errors.suburb)}
                    helperText={formik.touched.suburb && formik.errors.suburb}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required={formik.values.deliveryMethod === 'delivery'}
                    id="postcode"
                    name="postcode"
                    label="Postcode"
                    value={formik.values.postcode}
                    onChange={formik.handleChange}
                    error={formik.touched.postcode && Boolean(formik.errors.postcode)}
                    helperText={formik.touched.postcode && formik.errors.postcode}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="notes"
                    name="notes"
                    label="Delivery Notes (Optional)"
                    multiline
                    rows={3}
                    value={formik.values.notes}
                    onChange={formik.handleChange}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Payment Summary - Multiple Payments Support */}
            {orderData.orderId && (
              <PaymentSummary 
                key={paymentSummaryRefreshKey}
                orderId={orderData.orderId}
                customerEmail={orderData.customerDetails?.email || formik.values.email}
                onDataLoaded={handlePaymentSummaryLoaded}
                voucherOverride={voucherAmount}
                deliveryFeeOverride={deliveryFeeAmount}
                deliveryFeeLoading={deliveryFeeLoading}
                balanceOwingOverride={balanceOwing}
              />
            )}

            {/* Payment Method */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Payment Method
              </Typography>
              <FormControl component="fieldset" fullWidth>
                <RadioGroup
                  name="paymentMethod"
                  value={formik.values.paymentMethod}
                  onChange={formik.handleChange}
                >
                  {/* Credit Card - Show first if available */}
                  {!bpointLoading && bpointAvailable && (
                    <FormControlLabel
                      value="card_payment"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body1" fontWeight="bold">
                            Credit Card
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Pay securely using your card (Secured by Commonwealth Bank)
                          </Typography>
                        </Box>
                      }
                    />
                  )}
                  
                  {/* Direct Deposit - Always available */}
                  <FormControlLabel
                    value="direct_deposit"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body1" fontWeight="bold">
                          Direct Deposit / Bank Transfer
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Transfer funds directly to our bank account
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>

              {/* Payment Status Display - failed attempt takes precedence over previous success */}
              {paymentFailedAttempt && formik.values.paymentMethod === 'card_payment' && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <strong>Payment Failed</strong> - Your last card payment was declined. Please try again or use a different card.
                </Alert>
              )}
              {!paymentFailedAttempt && paymentStatus === 'paid' && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  <strong>Payment Completed</strong> - Your payment has been successfully processed.
                </Alert>
              )}
              {!paymentFailedAttempt && paymentStatus === 'pending' && formik.values.paymentMethod === 'card_payment' && !cardPaymentCompleted && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <strong>Payment Required</strong> - Please complete the card payment to proceed with your order.
                </Alert>
              )}

              {/* Direct Deposit Details */}
              {formik.values.paymentMethod === 'direct_deposit' && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Bank Details
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Account Name:</strong> NewLife Foodbarn
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>BSB:</strong> 064794
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Account Number:</strong> 10039415
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Reference:</strong> {orderData.orderNumber || 'Your Order Number'}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic' }}>
                    Please send confirmation of payment to: orders@newlifefoodbarn.org
                  </Typography>
                </Box>
              )}

              {/* Payment Amount Selector - Multiple Payments Support */}
              {formik.values.paymentMethod === 'card_payment' && 
               bpointAvailable && 
               paymentSummary && 
               balanceOwing > 0 && (
                <PaymentAmountSelector
                  key={resetPaymentSelector ? 'reset' : 'normal'}
                  balanceOwing={balanceOwing}
                  onChange={handlePaymentAmountChange}
                  defaultToFull={true}
                />
              )}

              {/* Card Payment with BPOINT - Only show if balance owing */}
              {formik.values.paymentMethod === 'card_payment' && 
               bpointAvailable && 
               orderData.orderId && 
               balanceOwing > 0 && (
                <Box sx={{ mt: 3 }}>
                  <BPointPayment
                    orderId={orderData.orderId}
                    amount={customPaymentAmount > 0 ? customPaymentAmount : balanceOwing}
                    deliveryFee={deliveryFeeAmount}
                    voucherAmount={voucherAmount}
                    parcelDayId={orderData.assignedParcelDay?.id ?? null}
                    parcelDayPickupSlotId={formik.values.deliveryMethod === 'pickup' ? (formik.values.pickupSlotId ?? null) : null}
                    deliveryMethod={formik.values.deliveryMethod}
                    onSuccess={handlePaymentSuccess}
                    onCancel={handlePaymentCancel}
                    onError={handlePaymentError}
                  />
                </Box>
              )}

              {/* Payment Complete Message */}
              {formik.values.paymentMethod === 'card_payment' && 
               bpointAvailable && 
               orderData.orderId && 
               balanceOwing <= 0 && (
                <Box sx={{ mt: 3 }}>
                  <Alert severity="success">
                    <AlertTitle>Payment Complete</AlertTitle>
                    Your order has been fully paid. No additional payment is required.
                  </Alert>
                </Box>
              )}
              
              {/* Card Payment Not Available */}
              {formik.values.paymentMethod === 'card_payment' && !bpointAvailable && !bpointLoading && (
                <Box sx={{ mt: 2 }}>
                  <Alert severity="info">
                    Card payment is not available for this order. Please select Direct Deposit.
                  </Alert>
                </Box>
              )}

              {/* Voucher Amount Input */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Voucher Amount (Optional)
                </Typography>
                <TextField
                  name="voucherAmount"
                  value={formik.values.voucherAmount}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter voucher amount (e.g., $50 or 50)"
                  fullWidth
                  size="small"
                  error={formik.touched.voucherAmount && Boolean(formik.errors.voucherAmount)}
                  helperText={formik.touched.voucherAmount && formik.errors.voucherAmount}
                  sx={{ maxWidth: 300 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Enter the total value of all vouchers you have. Vouchers cannot be used for delivery fees.
                </Typography>
              </Box>
            </Paper>

            {/* Terms and Conditions */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Terms and Conditions
              </Typography>
              <MuiFormControlLabel
                control={
                  <Checkbox
                    name="termsAccepted"
                    checked={formik.values.termsAccepted}
                    onChange={formik.handleChange}
                  />
                }
                label={
                  <Typography variant="body2">
                    I've read and agree to the{' '}
                    <Link
                      component={RouterLink}
                      to="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    >
                      website terms and conditions
                    </Link>
                  </Typography>
                }
              />
              {formik.touched.termsAccepted && formik.errors.termsAccepted && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                  {formik.errors.termsAccepted}
                </Typography>
              )}
              
              {/* Card Payment Validation Message (balance-centric) */}
              {formik.values.paymentMethod === 'card_payment' && balanceOwing > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Please pay the remaining balance (${balanceOwing.toFixed(2)}) or select Direct Deposit to place your order.
                </Alert>
              )}
              
              {/* Place Order and Cancel Order */}
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
                {onCancel && (
                  <Button variant="outlined" color="error" onClick={onCancel} disabled={cancelling}>
                    {cancelling ? 'Cancelling...' : 'Cancel Order'}
                  </Button>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={
                    loading ||
                    cancelling ||
                    !formik.isValid ||
                    !orderData.assignedParcelDay?.id ||
                    (formik.values.paymentMethod === 'card_payment' && balanceOwing > 0)
                  }
                  startIcon={loading ? <CircularProgress size={20} /> : null}
                >
                  {loading ? 'Processing...' : 'Place Order'}
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Back Button - Hide if any payment has been made (items locked) */}
        {totalPaid <= 0 && !paymentSummary?.items_locked && (
        <Box display="flex" justifyContent="flex-start" mt={4}>
          <Button variant="outlined" onClick={onBack}>
            Back
          </Button>
        </Box>
        )}
      </form>
    </Box>
  );
};

export default CheckoutPage; 